import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import collect from 'collect.js';
import { COUPON_TYPES, DropoffAction, ORDER_METHODS, REDUX_STORE_SLICES } from '../../services/exports/Constants';
import { Order, OrderData } from '../../services/exports/Interfaces';
import { OrderStoreInterface, StoreInterface } from '../types';

const initialOrder: Partial<OrderData> = {
  method: ORDER_METHODS.PICKUP,
  customer_note: '',
  delivery_street_name: null,
  delivery_street_number: null,
  delivery_city: null,
  delivery_state: null,
  delivery_country: null,
  delivery_company_name: null,
  delivery_doorbell_name: null,
  delivery_zip_code: null,
  delivery_lat: null,
  delivery_lng: null,
  delivery_dropoff_action: DropoffAction.RingDoorBell,
  room_service_details: null,
  promo: '',
  coupon_details: null,
  cashback_points: null,
  tip: undefined,
  scheduled_for: null,
  line_items: [],
  marketing_consent: false,
};

const orderSlice = createSlice({
  name: REDUX_STORE_SLICES.ORDER,
  initialState: {
    cached_order: initialOrder,
    ongoing_orders: [],
  },
  reducers: {
    updateOrder: (state: OrderStoreInterface, action: PayloadAction<Partial<OrderData>>) => {
      state.cached_order = { ...state.cached_order, ...action.payload };
    },
    resetOrder: (state: OrderStoreInterface, action?: PayloadAction<Partial<OrderData>>) => {
      const keep = [
        'customer_first_name',
        'customer_last_name',
        'customer_email',
        'customer_phone_number',
        'delivery_street_name',
        'delivery_street_number',
        'delivery_city',
        'delivery_state',
        'delivery_country',
        'delivery_zip_code',
        'delivery_dropoff_action',
        'delivery_doorbell_name',
        'delivery_company_name',
        'delivery_lat',
        'delivery_lng',
        // The method survives the reset, so the room/table it needs must too, or the next on-site
        // checkout sends no location and the order draft is refused (422 on room_service_location_id).
        'room_service_location_id',
        'room_service_location',
        'is_receipt_required',
        'invoice_company_name',
        'invoice_first_name',
        'invoice_last_name',
        'invoice_address',
        'invoice_zip_code',
        'invoice_city',
        'invoice_tax_id',
        'invoice_email',
      ];

      const data = {
        ...initialOrder,
        ...collect(state.cached_order).only(keep).items,
        method: state.cached_order?.method ?? ORDER_METHODS.PICKUP,
      };

      state.cached_order = {
        ...data,
        ...collect(action.payload).filter((value, key) => !data[key]).items,
      };
    },
    resetScheduledTime: (state: OrderStoreInterface, action?: PayloadAction<Partial<OrderData>>) => {
      state.cached_order = {
        ...state.cached_order,
        scheduled_for: action?.payload?.scheduled_for ?? null,
      };
    },
    addOngoingOrder: (state: OrderStoreInterface, action: PayloadAction<Order | null | undefined>) => {
      // A response that ended without a body (Firefox resolves a cut-off XHR as a success) is no order.
      if (!action.payload?.id) {
        return;
      }

      const index = collect(state.ongoing_orders).search((item) => item.id === action.payload.id);

      state.ongoing_orders = collect([...(state.ongoing_orders ?? [])])
        .when(
          index === false,
          (items) => items.prepend(action.payload),
          (items) => items.put(index, action.payload),
        )
        .toArray();
    },
    removeOngoingOrder: (state: OrderStoreInterface, action: PayloadAction<number>) => {
      state.ongoing_orders = collect(state.ongoing_orders).where('id', '!=', action.payload).toArray();
    },
  },
});

export const props = (state: OrderStoreInterface) => state;
export const selectOrderMethod = (state: StoreInterface) => state[REDUX_STORE_SLICES.ORDER].cached_order?.method;
export const selectCachedOrder = (state: StoreInterface) => state[REDUX_STORE_SLICES.ORDER].cached_order;
export const selectScheduledFor = (state: StoreInterface) =>
  state[REDUX_STORE_SLICES.ORDER].cached_order?.scheduled_for;
export const selectCouponType = (state: StoreInterface) => state[REDUX_STORE_SLICES.ORDER].cached_order?.coupon_type;
export const selectOngoingOrder = (state: StoreInterface) => state[REDUX_STORE_SLICES.ORDER]?.ongoing_orders?.[0];
export const selectHasCachedOrderPromoCode = (state: StoreInterface) =>
  state[REDUX_STORE_SLICES.ORDER]?.cached_order?.coupon_type === COUPON_TYPES.PROMO_CODE;
// A points redemption is active: coupon_type is CASHBACK and points are actually being spent.
export const selectHasCachedOrderPointsRedemption = (state: StoreInterface) =>
  state[REDUX_STORE_SLICES.ORDER]?.cached_order?.coupon_type === COUPON_TYPES.CASHBACK &&
  (state[REDUX_STORE_SLICES.ORDER]?.cached_order?.cashback_points ?? 0) > 0;
export const selectPaymentProps = createSelector(
  (state) => state[REDUX_STORE_SLICES.ORDER]?.cached_order,
  (order: OrderData) => ({
    payment_method_id: order?.payment_method_id,
    payment_intent_id: order?.payment_intent_id,
    customer_phone_number: order?.customer_phone_number,
    customer_email: order?.customer_email,
    customer_first_name: order?.customer_first_name,
    customer_last_name: order?.customer_last_name,
    is_receipt_required: order?.is_receipt_required,
    invoice_company_name: order?.invoice_company_name,
    invoice_first_name: order?.invoice_first_name,
    invoice_last_name: order?.invoice_last_name,
    invoice_address: order?.invoice_address,
    invoice_zip_code: order?.invoice_zip_code,
    invoice_city: order?.invoice_city,
    invoice_tax_id: order?.invoice_tax_id,
    invoice_email: order?.invoice_email,
  }),
);

export default orderSlice;
